import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './Screensaver.css';
import useGlobalStore from '../../store/globalStore';

import nextButtonImage from '../../assets/UI/white_arrow.svg'; // Adjust path if needed
import logoImage from '../../assets/UI/logo_new.png';
import { fixURL } from '../../utils/urlUtils';

import parse from 'html-react-parser';

const Screensaver = ({
  slides,
  disableTouchFlag,
  onClose,
  displayDuration = 8000, // how long to display each image
  transitionDuration = 500, // This is for image cross-fade
  overlayTransitionDuration = 300 // For overlay fade in/out
}) => {
  
  // State for two image layers for smooth transitions and zoom preservation
  const [imageLayers, setImageLayers] = useState([
    { id: 'A', src: null, type: 'image', keySuffix: 0, style: { opacity: 0, zIndex: 1, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' } },
    { id: 'B', src: null, type: 'image', keySuffix: 0, style: { opacity: 0, zIndex: 1, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' } },
  ]);
  // Use a ref for topLayerId to avoid re-triggering useEffect unnecessarily
  const topLayerIdRef = useRef('A'); // Layer A will be the first to show
  const [currentSlideDataIndex, setCurrentSlideDataIndex] = useState(0); // Index in imageSlides array

  // State for the overlay visibility and fade effect
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const tempVideoRef = useRef(null);

  // global state for disabled touch for electron + screensaver mode
  const disableTouchScreensaver = useGlobalStore((state) => state.disableTouchScreensaver);
  const disableTouchScreensaverTitle = useGlobalStore((state) => state.screensaverTitle);
  const disableTouchScreensaverSubheading = useGlobalStore((state) => state.screensaverSubheading);

  const cycleTimerRef = useRef(null); // Timer for scheduling the next slide change
  const afterTransitionTimerRef = useRef(null); // Timer for actions after fade-in completes
  const closeTimerRef = useRef(null); // Timer for delayed close action
  const isInitialSetupDoneRef = useRef(false); // Guard for initial setup logic

  const imageSlides = useMemo(() => {
    if(slides && slides.hasOwnProperty("attract_state_media")) {
      const slidesMedia = slides.attract_state_media;
      return (slidesMedia || []).filter(slide =>
        slide && (slide.full_resolution_image || slide.video_file || slide.thumbnail_src || slide.thumbnail)
      );
    }
    return [];
    
  }, [slides]);

  // FIXME: this is not firing
  // const onVideoComplete = (e) => {
  //   console.log("screensaver onVideoComplete",e);
  //   console.log(tempVideoRef);
  //   if(tempVideoRef.current) {
  //     console.log("now need to play video from start?");
  //   }
  // };
  const onVideoComplete = useCallback(() => {
    console.log("screensaver onVideoComplete", imageSlides.length);

    // optional looping if only 1 item in slides- otherwise it just freezes on last frame of video
    if (tempVideoRef.current && imageSlides.length <= 1) {
      tempVideoRef.current.currentTime = 0;
      tempVideoRef.current.play();
    } else {
      // trigger next slide at end of video played
      setCurrentSlideDataIndex(prevIdx => {
          //const curSlide = imageSlides[prevIdx];
          // update: if the current slide is a video- we never increment the index to goto next slide. this is handled in the onvideoended event
          //console.log('3a. Cur index before update (video):', prevIdx, curSlide.type);
          const newIdx = (prevIdx + 1) % imageSlides.length;
          //const nextSlide = imageSlides[newIdx];
          //console.log('3b. New index after update (video):', newIdx, nextSlide.type);
          // You can also perform other actions with newIdx here if needed
          return newIdx;
        });
    }
  }, [imageSlides]);

  const handleClose = useCallback(() => {
    console.log("handleClose", disableTouchScreensaver, disableTouchFlag)
    if(disableTouchScreensaver) {
      console.log("Screensaver: Screensaver touch disabled.");
      return;
    }

    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
    if (afterTransitionTimerRef.current) clearTimeout(afterTransitionTimerRef.current);

    setIsOverlayVisible(false); // Start fade-out of the overlay

    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      if (onClose) onClose();
    }, overlayTransitionDuration);
  }, [onClose, overlayTransitionDuration, disableTouchScreensaver, disableTouchFlag]);

  // Effect for handling Escape key and initial overlay fade-in
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    const fadeInTimeout = requestAnimationFrame(() => {
      setIsOverlayVisible(true);
    });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      cancelAnimationFrame(fadeInTimeout);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [handleClose]);

  // Effect for managing the image cycling and transitions
  useEffect(() => {
    if (!isOverlayVisible || !imageSlides || imageSlides.length === 0) {
      // Clear timers and reset initial setup flag if screensaver is not active or no images
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      if (afterTransitionTimerRef.current) clearTimeout(afterTransitionTimerRef.current);
      isInitialSetupDoneRef.current = false; // Reset for next time screensaver opens
      if (isOverlayVisible && (!imageSlides || imageSlides.length === 0)) {
        handleClose();
      }
      return;
    }

    // Initial setup logic - runs only once per screensaver session
    if (!isInitialSetupDoneRef.current) {
      console.log("Screensaver: Performing initial setup.", imageSlides.length);
      const initialSlideData = imageSlides[0]; // Start with the first image
      const initialSrc = initialSlideData.full_resolution_image || initialSlideData.video_file || initialSlideData.thumbnail_src || initialSlideData.thumbnail;
      const initialType = initialSlideData.type;
      console.log("setup ss type", initialType);

      setCurrentSlideDataIndex(0); // Ensure index is 0
      topLayerIdRef.current = 'A'; // Layer A is initially on top

      setImageLayers(prevLayers =>
        prevLayers.map(layer =>
          layer.id === topLayerIdRef.current
            ? { ...layer, src: initialSrc, type:initialType, keySuffix: layer.keySuffix + 1, style: { ...layer.style, opacity: 1, zIndex: 1, transition: 'none' } }
            : { ...layer, src: null, style: { ...layer.style, opacity: 0, zIndex: 2 } } // Layer B is ready
        ));

      isInitialSetupDoneRef.current = true;

      // Schedule the first transition (to the *next* slide)
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = setTimeout(() => {
        // This will trigger a change in currentSlideDataIndex, causing this useEffect to run again
        // for the actual first transition.
        //console.log("cycleTimerRef: ", currentSlideDataIndex);
        //setCurrentSlideDataIndex(prevIdx => (prevIdx + 1) % imageSlides.length);
        setCurrentSlideDataIndex(prevIdx => {
          const curSlide = imageSlides[prevIdx];
          // update: if the current slide is a video- we never increment the index to goto next slide. this is handled in the onvideoended event
          //console.log('1a. Cur index before update:', prevIdx, curSlide.type);
          const newIdx = (curSlide.type !== "video") ? (prevIdx + 1) % imageSlides.length : prevIdx;
          //const nextSlide = imageSlides[newIdx];
          //console.log('1b. New index after update:', newIdx, nextSlide.type);
          // You can also perform other actions with newIdx here if needed
          return newIdx;
        });
        //console.log("next cycleTimerRef: ", currentSlideDataIndex);

      }, displayDuration);
      return; // End of initial setup path
    }
    
    // --- FIX: Prevent redundant transitions ---
    // This can happen if an unstable dependency causes the effect to re-run for the initial slide.
    // We check if the slide we're asked to transition to is already the one on top.
    const slideDataForCurrentTransition = imageSlides[currentSlideDataIndex];
    const srcForCurrentTransition = slideDataForCurrentTransition?.full_resolution_image || slideDataForCurrentTransition?.video_file || slideDataForCurrentTransition?.thumbnail_src || slideDataForCurrentTransition?.thumbnail;
    const typeForCurrentTransition = slideDataForCurrentTransition?.type;
    const currentTopLayer = imageLayers.find(l => l.id === topLayerIdRef.current);

    if (currentTopLayer?.src === srcForCurrentTransition) {
      console.log(`Screensaver: Skipping transition to slide ${currentSlideDataIndex} as it's already visible.`);
      // We still must schedule the next slide change, otherwise a single-slide show would halt.
      // Instead of resetting the timer, immediately advance to the next slide
      // and let the normal timer logic take over from there.
      setCurrentSlideDataIndex(prevIdx => (prevIdx + 1) % imageSlides.length);

      return; // Exit to prevent the transition logic from running
    }
    // --- END FIX ---

    // This part runs for subsequent transitions (after initial setup)
    // It's triggered when currentSlideDataIndex changes.
    console.log(`Screensaver: Transitioning to slide index ${currentSlideDataIndex}. Current top layer: ${topLayerIdRef.current}, ${imageSlides.length}`);

    if (!slideDataForCurrentTransition) {
      console.error("Screensaver: Slide data not found for current index.", currentSlideDataIndex);
      return; // Should not happen if imageSlides is valid
    }

    const layerToFadeInId = topLayerIdRef.current === 'A' ? 'B' : 'A';
    const baseStyle = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' };

    // 1. Prepare the layer that will fade in
    setImageLayers(prevLayers =>
      prevLayers.map(layer => {
        if (layer.id === layerToFadeInId) {
          return {
            ...layer,
            src: srcForCurrentTransition,
            type: typeForCurrentTransition,
            keySuffix: layer.keySuffix + 1,
            style: { ...baseStyle, opacity: 0, zIndex: 2, transition: `opacity ${transitionDuration}ms ease-in-out` }
          };
        }
        // The currently visible layer (topLayerIdRef.current) remains as is, acting as the background for the fade.
        // Ensure it's zIndex 1 and opaque.
        if (layer.id === topLayerIdRef.current) {
            return { ...layer, style: { ...baseStyle, opacity: 1, zIndex: 1, transition: 'none'} };
        }
        return layer; // Should ideally not be reached if only two layers
      })
    );

    // 2. After a tick (to ensure styles are applied), trigger the fade-in
    requestAnimationFrame(() => {
      setImageLayers(prevLayers =>
        prevLayers.map(layer =>
          layer.id === layerToFadeInId
            ? { ...layer, style: { ...layer.style, opacity: 1 } } // .style already has the transition
            : layer
        )
      );
    });

    // 3. Update which layer is considered "top"
    topLayerIdRef.current = layerToFadeInId;

    // 4. After the fade-in transition completes...
    if (afterTransitionTimerRef.current) clearTimeout(afterTransitionTimerRef.current);
    afterTransitionTimerRef.current = setTimeout(() => {
      //console.log("*** afterTransitionTimerRef", currentSlideDataIndex);

      // `topLayerIdRef.current` now refers to the layer that just finished fading in.
      // Make it the stable base. Reset the other layer.
      setImageLayers(prevLayers =>
        prevLayers.map(layer => {
          if (layer.id === topLayerIdRef.current) { // The one that just faded in
            return { ...layer, style: { ...baseStyle, opacity: 1, zIndex: 1, transition: 'none' } };
          } else { // The one that was behind
            return { ...layer, src: null, style: { ...baseStyle, opacity: 0, zIndex: 2, transition: 'none' } };
          }
        })
      );

      // 5. Schedule the next index advancement
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = setTimeout(() => {
        //console.log("*** cycleTimerRef");
        //setCurrentSlideDataIndex(prevIdx => (prevIdx + 1) % imageSlides.length);
        setCurrentSlideDataIndex(prevIdx => {
          const curSlide = imageSlides[prevIdx];
          // update: if the current slide is a video- we never increment the index to goto next slide. this is handled in the onvideoended event
          //console.log('2a. Cur index before update:', prevIdx, curSlide.type);
          const newIdx = (curSlide.type !== "video") ? (prevIdx + 1) % imageSlides.length : prevIdx;
          //const nextSlide = imageSlides[newIdx];
          //console.log('2b. New index after update:', newIdx, nextSlide.type);
          // You can also perform other actions with newIdx here if needed
          return newIdx;
        });
      }, displayDuration);

    }, transitionDuration);

    return () => { // Cleanup
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      if (afterTransitionTimerRef.current) clearTimeout(afterTransitionTimerRef.current);
    };
  }, [imageSlides, displayDuration, transitionDuration, isOverlayVisible, handleClose,
    currentSlideDataIndex // topLayerId is now a ref, not a dependency for re-running the effect body
  ]);


  if (!isOverlayVisible && (!imageSlides || imageSlides.length === 0)) {
    return null;
  }

  //const videoControlsList = 'nofullscreen nodownload noplaybackrate muted loop'; // dont show fullscreen button in electron app- breaks the custom zoom
  return (
    <div
      className={`screensaver-overlay ${isOverlayVisible ? 'visible' : ''}`}
      onClick={handleClose}
      role="button"
      tabIndex={0}
      aria-label="Close screensaver"
    >
      {isOverlayVisible && slides && (
        <div className="screensaver-text-content">
          <div className="screensaver-heading">
            {/* {disableTouchScreensaver ? <>New exhibition coming soon</> : <>Future of Health{slides.title}</>} */}
            {disableTouchScreensaver ? <>{disableTouchScreensaverTitle}</> : <>{slides.title}</>}
          </div>
          <div className="screensaver-caption">
            {/* {disableTouchScreensaver ? <>Opening November 2025</> : <>Explore our exhibition on the future of health {slides.attract_state_subheading}</>} */}
            {disableTouchScreensaver ? <>{disableTouchScreensaverSubheading}</> : <>{slides.attract_state_subheading}</>}
          </div>
          {!disableTouchFlag &&
          <div className="screensaver-prompt">
            Touch to begin <span className="screensaver-prompt-icon"><img src={nextButtonImage} alt="Next" /></span>
          </div>
          }
        </div>
      )}

      {/* Render the two image layers. Their src, key, opacity, and zIndex are managed by state. */}
      {imageLayers.map(layer => (
        layer.src && layer.type === 'image' ? (
          <img
            key={`screensaver-img-${layer.id}-${layer.keySuffix}-${layer.type}`}
            src={fixURL(layer.src)}
            alt={`Screensaver background ${layer.id}`}
            className="screensaver-image"
            style={layer.style}
          />
        ) : layer.src && layer.type === 'video' ? (
          <div className="video-element-wrapper2" key={`screensaver-video-${layer.id}-${layer.keySuffix}-${layer.type}`} style={layer.style}>
              <video
                ref={tempVideoRef} // Use callback ref
                key={layer.src} // Force re-render on src change
                src={ fixURL(layer.src) }
                // controls
                //controlsList={videoControlsList}
                disablePictureInPicture
                muted
                //loop
                autoPlay // Consider making this optional via prop
                crossOrigin="anonymous"
                className="popup-video2"
                preload="metadata" // Good default: loads dimensions, duration etc.
                // Event listeners are now handled programmatically in useEffect
                onEnded={onVideoComplete}
              ></video>
          </div>
        ) : null
      ))}

      <div className='screensaver-grad-overlay'>
        <img src={logoImage} alt="Logo" className="screensaver-logo" />
      </div>
    </div>
  );
};

export default Screensaver;
